import { ChangeEvent } from 'react'
import { FieldValues, UseFormReturn } from 'libs/form'
import { useTranslation } from 'libs/i18n'
import { Switch } from 'shared/components'
import { cn } from 'utils/style'

type ProductRegistrationFieldsProps = {
	form: UseFormReturn<FieldValues>
}

type Toggle = {
	name: string
	label: string
	description: string
	indented?: boolean
	disabled?: boolean
	onChange?: (event: ChangeEvent<HTMLInputElement>) => void
}

type ToggleGroup = {
	title: string
	toggles: Toggle[]
}

export const ProductRegistrationFields = ({ form }: ProductRegistrationFieldsProps) => {
	const { t } = useTranslation()
	const { register, setValue, watch } = form
	const usersCanPublish = watch('users_can_publish_products')

	const handlePublishChange = (event: ChangeEvent<HTMLInputElement>) => {
		setValue('users_can_publish_products', event.target.checked)

		if (!event.target.checked) setValue('default_product_publish_value', false)
	}

	const groups: ToggleGroup[] = [
		{
			title: t('Publishing'),
			toggles: [
				{
					name: 'users_can_publish_products',
					label: t('Show publish product button for users'),
					description: t(
						'Allow users to decide if the product can be published. If Disabled, only store administrators can make products published on web.'
					),
					onChange: handlePublishChange
				},
				{
					name: 'default_product_publish_value',
					label: t('Publish button is on by default'),
					description: t('If Enabled, products will be published automatically'),
					indented: true,
					disabled: !usersCanPublish
				},
				{
					name: 'photos_required_to_publish',
					label: t('Require photos before publish'),
					description: t('Require users to set a photo for each product before it can be published')
				}
			]
		},
		{
			title: t('User permissions'),
			toggles: [
				{
					name: 'quantity_enabled',
					label: t('Allow users to set quantity'),
					description: t(
						'Allow users to set a total quantity available for each product (if disabled, default quantity is 1)'
					)
				},
				{
					name: 'categories_enabled',
					label: t('Allow users to set categories'),
					description: t(
						'Allow users to set a category for each product (if disabled, category field is hidden)'
					)
				},
				{
					name: 'tags_enabled',
					label: t('Allow users to set tags'),
					description: t(
						'Allow users to set tags for each product  (if disabled, tags field is hidden)'
					)
				},
				{
					name: 'color_enabled',
					label: t('Allow users to set color'),
					description: t(
						'Allow users to set a color for each product  (if disabled, color field is hidden)'
					)
				},
				{
					name: 'cost_price_enabled',
					label: t('Allow store to set cost price'),
					description: t(
						'Allow users to set the cost price for each product (if disabled, cost price field is disabled)'
					)
				}
			]
		},
		{
			title: t('Product information fields'),
			toggles: [
				{
					name: 'product_conditions_enabled',
					label: t('Condition'),
					description: t(
						'Allow users to set a condition for each product (if disabled, condition field is hidden).'
					)
				},
				{
					name: 'product_fabrics_enabled',
					label: t('Fabric'),
					description: t(
						'Allow users to set a fabric for each product (if disabled, fabric field is hidden).'
					)
				},
				{
					name: 'product_genders_enabled',
					label: t('Gender'),
					description: t(
						'Allow users to set a gender for each product (if disabled, gender field is hidden).'
					)
				},
				{
					name: 'product_types_enabled',
					label: t('Type'),
					description: t(
						'Allow users to set a type for each product (if disabled, type field is hidden).'
					)
				},
				{
					name: 'supplier_sku_barcode_enabled',
					label: t('Supplier SKU and barcode'),
					description: t(
						'Allow users to set a supplier SKU and barcode for each product (if disabled, the fields are hidden).'
					)
				},
				{
					name: 'vat_enabled',
					label: t('VAT'),
					description: t(
						'Allow users to set VAT for each product (if disabled, VAT field is hidden).'
					)
				},
				{
					name: 'product_confirm_enabled',
					label: t('Product confirmation'),
					description: t('Enable subproduct confirmation services for this store')
				}
			]
		}
	]

	return (
		<>
			<div className="flex items-center gap-2.5">
				<span aria-hidden className="h-5 w-1 rounded-full bg-primary" />
				<h2 className="font-display text-[15px] font-semibold text-foreground">
					{t('Product Registration')}
				</h2>
			</div>
			<p className="text-[12px] leading-relaxed text-muted-foreground">
				{t('Control what users can see and do when they register products in your store.')}
			</p>
			{groups.map((group) => (
				<section key={group.title}>
					<h3 className="mb-2 font-display text-[13px] font-semibold text-foreground">
						{group.title}
					</h3>
					<div className="divide-y divide-border rounded-lg border border-border bg-muted/20 px-4">
						{group.toggles.map(({ name, indented, onChange, ...toggle }) => {
							const field = register(name)

							return (
								<div
									key={name}
									className={cn(
										'py-3.5',
										indented && 'ml-5 border-l-2 border-border pl-4 sm:ml-7'
									)}>
									<Switch {...toggle} {...field} onChange={onChange ?? field.onChange} />
								</div>
							)
						})}
					</div>
				</section>
			))}
		</>
	)
}
